import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Tooltip,
  Alert,
} from '@mui/material';
import { abyssColors } from '@/theme/colors';
import { TablePaginationContainer } from '../../styles';
import { defaultPaginationSize } from '@/constants';
import { TablePagination } from '../../../shared/TablePagination';

type Props = {
  permissions: { name: string; description: string; count: number; roles: string[] }[];
  directPermissions: string[];
};

const UserPermissions = ({ permissions, directPermissions }: Props) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleChangePage = (newPage: number) => setPage(newPage);

  const tableHeaders = ['Name', 'Description', 'Assignment'];

  const filteredPermissions = permissions.filter(({ name, roles }) => {
    const isDirectlyAssigned = directPermissions.includes(name);
    const isPartOfRole = roles.length > 0;

    return isDirectlyAssigned || isPartOfRole;
  });

  const paginatedPermissions = filteredPermissions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (permissions.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No permissions exist with this role
      </Alert>
    );
  }

  return (
    <TableContainer sx={{ '& thead th:nth-child(1)': { width: '40%' } }}>
      <Table>
        <TableHead>
          <TableRow>
            {tableHeaders.map((header) => (
              <TableCell key={header}>
                <Typography fontWeight={600}>{header}</Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {paginatedPermissions.map(({ name, description, count, roles }) => {
            const isDirectlyAssigned = directPermissions.includes(name);

            return (
              <TableRow key={name}>
                <TableCell>{name}</TableCell>
                <TableCell sx={{ width: '100%' }}>{description}</TableCell>
                <TableCell sx={{ display: 'flex', minWidth: 200 }}>
                  {roles.length > 0 && (
                    <Tooltip
                      title={
                        <Typography variant="body2" sx={{ padding: '8px', color: 'white' }}>
                          {roles.join(', ')}
                        </Typography>
                      }
                      arrow
                      placement="top"
                      sx={{
                        bgcolor: abyssColors.primary[500],
                        color: 'white',
                        fontSize: '0.875rem',
                        '& .MuiTooltip-arrow': {
                          color: abyssColors.primary[500],
                        },
                      }}
                    >
                      <Chip
                        label={`${count} role${count > 1 ? 's' : ''}`}
                        size="medium"
                        sx={{
                          backgroundColor: abyssColors.primary[500],
                          color: 'white',
                          marginRight: '8px',
                        }}
                      />
                    </Tooltip>
                  )}

                  {isDirectlyAssigned && (
                    <Chip
                      label="Directly Assigned"
                      size="medium"
                      sx={{
                        backgroundColor: abyssColors.secondary[500],
                        color: 'white',
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePaginationContainer>
        <TablePagination
          count={filteredPermissions.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[
            ...defaultPaginationSize,
            { label: 'All', value: filteredPermissions.length },
          ]}
        />
      </TablePaginationContainer>
    </TableContainer>
  );
};

export default UserPermissions;
